import { useLayoutEffect, useRef } from 'react'

/**
 * Keys pressed while a game is on screen.
 *
 * There is a third way to play this: a Raspberry Pi on the television, a
 * browser in kiosk mode, and a Bluetooth keyboard on the sofa. No mouse, no
 * touchscreen. That is a higher bar than being reachable by tabbing — eleven
 * tab stops passes an audit and fails a person on a sofa — so every game
 * listens for the keys somebody would actually press.
 *
 * Two things are filtered out before the handler ever sees them:
 *
 * A key held with Ctrl, Meta or Alt belongs to the browser or the operating
 * system. Treating Ctrl+R as the letter R would reload the page and eat a
 * guess on the way past.
 *
 * A key pressed inside a text field belongs to the field. Typing "cat" into the
 * room-code box should not also be three guesses.
 *
 * And Enter or Space pressed while a button or a link has focus belongs to that
 * control. Without this a game holding Enter swallowed it everywhere on the
 * page — somebody who had tabbed to the way home could not activate it, which
 * on a kiosk with no back button leaves them stuck in the game. Letters and
 * digits still reach the game, because a button does nothing with those.
 *
 * **Attached before the paint, not after it.** A passive effect runs after the
 * browser has drawn, so there is a window in which the question is on screen
 * and nothing is listening — the game looks ready and is not, and the first
 * key pressed in that window is dropped without trace.
 *
 * It is a narrow window and no child would reach it: a person cannot see a
 * question and press a key inside one frame. A test can, and did. Monkey's
 * keyboard walkthrough failed in CI about once in ten, always on the very
 * first question and always with the answer simply missing — reproduced 5
 * times in 250 questions with the CPU throttled, and not once in 250 after
 * this. It also widens on a machine slow enough to have a long task between
 * the paint and the flush, which is what the Pi under the television is.
 *
 * A layout effect is the right tool rather than a trick: "be listening as soon
 * as you are visible" is exactly what it means.
 */
export function useGameKeys(handler: (event: KeyboardEvent) => void): void {
  // Held in a ref so a caller passing an inline function does not rebind the
  // listener on every render — the same reason the socket callbacks are.
  const latest = useRef(handler)

  // Before the paint too, and for the same reason: a key pressed between the
  // render and a passive flush would otherwise reach the handler the screen
  // has already stopped showing.
  useLayoutEffect(() => {
    latest.current = handler
  })

  useLayoutEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (event.metaKey || event.ctrlKey || event.altKey) return
      if (isTyping(event.target)) return
      if (activatesFocusedControl(event)) return

      latest.current(event)
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])
}

/** Whether the key belongs to something being typed into. */
export function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false

  return (
    target.isContentEditable ||
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement
  )
}

/** Whether this key is the one that presses whatever currently has focus. */
function activatesFocusedControl(event: KeyboardEvent): boolean {
  if (event.key !== 'Enter' && event.key !== ' ') return false

  const focused = document.activeElement
  if (!(focused instanceof HTMLElement)) return false

  return (
    focused instanceof HTMLButtonElement ||
    (focused instanceof HTMLAnchorElement && focused.hasAttribute('href')) ||
    focused.getAttribute('role') === 'button' ||
    focused.getAttribute('role') === 'link'
  )
}

/** A single letter, or null for anything else. Case folded, since a key is a key. */
export function letterFrom(event: KeyboardEvent): string | null {
  return /^[a-zA-Z]$/.test(event.key) ? event.key.toLowerCase() : null
}

/** A single digit, or null. */
export function digitFrom(event: KeyboardEvent): string | null {
  return /^[0-9]$/.test(event.key) ? event.key : null
}
